import { Controller } from "@hotwired/stimulus"

const WEEKDAYS = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"]
const parse = (iso) => (iso ? new Date(`${iso}T00:00:00`) : null)
const iso = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
const short = (date) => date.toLocaleDateString("en-US", { month: "short", day: "numeric" })

// Two months side by side: tap the first day, then the last (or Done for a single day). Writes the
// form's start and end date fields, and tells the form they changed.
export default class extends Controller {
  static targets = ["trigger", "label", "popover", "months", "hint", "start", "end"]
  static values = { min: String, max: String, month: String }

  connect() {
    this.from = parse(this.startTarget.value)
    this.to = parse(this.endTarget.value)
    if (this.to && this.from && this.to.getTime() === this.from.getTime()) this.to = null
    this.month = parse(this.monthValue) || new Date()
    this.month.setDate(1)
    this.paintLabel()
  }

  open() {
    this.popoverTarget.hidden = false
    this.render()
    this.popoverTarget.scrollIntoView({ block: "nearest" })
  }

  close() {
    this.popoverTarget.hidden = true
  }

  closeOutside(event) {
    // A day or hour that was just picked has been redrawn, so it's no longer in the page
    if (!this.popoverTarget.hidden && event.target.isConnected && !this.element.contains(event.target)) this.close()
  }

  previous() {
    this.month = new Date(this.month.getFullYear(), this.month.getMonth() - 1, 1)
    this.render()
  }

  next() {
    this.month = new Date(this.month.getFullYear(), this.month.getMonth() + 1, 1)
    this.render()
  }

  pick(event) {
    const day = parse(event.currentTarget.dataset.day)
    if (!this.from || this.to || day < this.from) {
      this.from = day
      this.to = null
    } else {
      this.to = day.getTime() === this.from.getTime() ? null : day
    }
    this.write()
    this.render()
  }

  clear() {
    this.from = this.to = null
    this.write()
    this.render()
  }

  apply() {
    this.close()
  }

  write() {
    this.startTarget.value = this.from ? iso(this.from) : ""
    // A one-day event ends on its first day, so its closing time is kept
    this.endTarget.value = this.to ? iso(this.to) : this.from ? iso(this.from) : ""
    this.paintLabel()
    this.startTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  paintLabel() {
    if (!this.from) {
      this.labelTarget.textContent = "Choose dates"
      this.triggerTarget.classList.remove("is-set")
      return
    }
    const year = (this.to || this.from).getFullYear()
    this.labelTarget.textContent = this.to ? `${short(this.from)} – ${short(this.to)}, ${year}` : `${short(this.from)}, ${year}`
    this.triggerTarget.classList.add("is-set")
  }

  render() {
    const min = parse(this.minValue)
    const max = parse(this.maxValue)
    this.hintTarget.textContent = !this.from || this.to ? "Tap the first day" : "Now the last day, or Done for one day"
    this.monthsTarget.innerHTML = [0, 1].map((offset) => {
      const first = new Date(this.month.getFullYear(), this.month.getMonth() + offset, 1)
      const blanks = (first.getDay() + 6) % 7
      const days = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate()
      const cells = Array.from({ length: blanks }, () => "<span></span>")
      for (let number = 1; number <= days; number++) {
        const day = new Date(first.getFullYear(), first.getMonth(), number)
        const disabled = (min && day < min) || (max && day > max)
        const ends = [this.from, this.to].some((edge) => edge && edge.getTime() === day.getTime())
        const between = this.from && this.to && day > this.from && day < this.to
        cells.push(`<button type="button" class="v2-day${ends ? " is-end" : ""}${between ? " is-between" : ""}" data-day="${iso(day)}"
          data-action="date-range#pick" ${disabled ? "disabled" : ""} aria-pressed="${ends}">${number}</button>`)
      }
      return `<div>
        <p class="text-center text-headline text-stone-900">${first.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</p>
        <div class="mt-3 grid grid-cols-7 gap-y-0.5 text-center sm:gap-y-1">
          ${WEEKDAYS.map((name) => `<span class="v2-label pb-1">${name}</span>`).join("")}
          ${cells.join("")}
        </div>
      </div>`
    }).join("")
  }
}
